export interface Account {
  _id: string
  first_name: string
  last_name: string
  email: string
  phone: string
  username?: string
  company_name?: string
  trial_period_end?: string
  is_locked?: boolean
  is_active?: boolean
  monthly_payment_amount?: number
  monthly_payment_include_vat?: boolean
  transaction_denial?: boolean
  // Card on file — the tokens themselves are never sent to the client, only
  // whether one exists and its last 4 digits (see GET /admin/accounts/:id).
  has_payment_token?: boolean
  payment_card_last4?: string
  marketplace_enabled?: boolean
  watermark_enabled?: boolean
  watermark_override?: { anchor?: string; offsetXPercent?: number; offsetYPercent?: number; logoSizePercent?: number; textSizePercent?: number }
  group_scheduling_enabled?: boolean
  xpost_spread_across_day?: boolean
  // limited profiles post to groups anonymously instead of xpost
  anon_post_when_limited?: boolean
  // true = this client has opted out of xpost seed groups entirely
  seed_group_disabled?: boolean
  // PayPal subscriptions: per-customer opt-in (default off) + mirrored subscription state
  paypal_enabled?: boolean
  paypal_subscription?: { id?: string; status?: string; period?: 'monthly' | 'yearly'; price?: number; next_billing_at?: string; last_payment_at?: string; payer_email?: string } | null
  stats_real_numbers?: boolean
  marketplace_checkbox_enabled?: boolean
  api_access_enabled?: boolean
  library_occupation?: string | null
  email_verified?: boolean
  schedule_enabled?: boolean
  dynamic_options?: {
    post_footer_enabled?: boolean
    post_footer_position?: 'start' | 'end' | null
    [key: string]: any
  }
  account_type?: string
  created_at: string
  updated_at?: string
  profile_count?: number
  active_profiles?: number
  vip_account?: boolean
  personal_plan?: boolean
  customer_info?: {
    customer_description?: string
    account_status?: string
    occupation?: string
    account_manager?: AdminUser
  }
  referral_account?: {
    _id: string
    first_name: string
    last_name: string
    email: string
  }
  notification_settings?: {
    notifications_on_whatsapp?: boolean
    whatsapp_phone_number?: string
  }
}

export interface FacebookProfile {
  _id: string
  first_name?: string
  last_name?: string
  facebook_id?: string
  facebook_username?: string
  last_health_check?: string
  parent_profile?: string
  parent_profile_on?: boolean
  is_logged_in?: boolean
  seed?: string
  logging_stats?: any
  posts_per_day?: number
}

export interface ClientLog {
  _id: string
  account: string
  created_by?: AdminUser
  subject?: string
  description?: string
  action?: string
  is_pinned?: boolean
  pinned_type?: string
  created_at: string
  updated_at?: string
}

export interface Invoice {
  _id: string
  account: string
  amount: number
  description?: string
  status: 'pending' | 'completed' | 'failed'
  payment_method?: string
  created_at: string
}

export interface AdminUser {
  _id: string
  username: string
  email: string
}

export interface AccountStats {
  totalAccounts: number
  activeAccounts: number
  trialAccounts: number
  marketplaceAccounts: number
}

export interface AccountsQueryResponse {
  accounts: Account[]
  pagination: {
    page: number
    limit: number
    total: number
    pages: number
  }
}

export interface AccountDetailResponse {
  account: Account
  profiles: FacebookProfile[]
  logs: ClientLog[]
  invoices: Invoice[]
}

export interface LogsQueryResponse {
  logs: ClientLog[]
  pagination: {
    page: number
    limit: number
    total: number
    pages: number
  }
}

export const ACCOUNT_STATUS_LABELS: Record<string, string> = {
  active: 'פעיל',
  inactive: 'לא פעיל',
  locked: 'נעול',
  trial: 'תקופת ניסיון',
  expired: 'פג תוקף'
}

export const getAccountStatus = (account: Account): string => {
  if (account.is_locked) return 'locked'
  if (!account.trial_period_end) return 'inactive'
  const trialEnd = new Date(account.trial_period_end)
  if (trialEnd < new Date()) return 'expired'
  if (account.monthly_payment_amount && account.monthly_payment_amount > 0) return 'active'
  return 'trial'
}

export const getAccountStatusLabel = (account: Account): string => {
  return ACCOUNT_STATUS_LABELS[getAccountStatus(account)] || 'לא ידוע'
}

export const getAccountStatusColor = (account: Account): string => {
  const status = getAccountStatus(account)
  switch (status) {
    case 'active': return 'success'
    case 'trial': return 'info'
    case 'locked': return 'danger'
    case 'expired': return 'warning'
    default: return 'secondary'
  }
}
